import React from "react";
import { CreditCard, ShoppingCart, AlertTriangle, Info } from "lucide-react";

/* ══════════════════════════════════════════════════════════════════════════
   Billing — summary rail, spend chart and the gateway-aware plumbing behind
   them. Split out of SettingsV2 (already 5.6k lines) so the billing surface
   can be read on its own.

   The important thing here is that Stripe and Razorpay are NOT symmetrical,
   and the UI has to stop pretending they are:

                        Stripe                     Razorpay
     payment method     /billing/payment-methods   no endpoint — read the
                        (brand + last4)            instrument off the last
                                                   captured payment
     manage             hosted customer portal     no portal — cancel/resume
                                                   handled in-app
     invoices           /billing/invoices          /razorpay-billing/invoices
     currency           usually USD                INR

   Everything below resolves those differences in one place rather than
   scattering `paymentGateway === 'razorpay'` checks through the JSX.
   ══════════════════════════════════════════════════════════════════════════ */

/* Motion, kept local. The graphical dashboard's WowMotion sheet has most of
   these, but importing it would couple Settings to those screens for the sake
   of six keyframes — and it has no fill/tail rules anyway. */
const MOTION_CSS = `
@keyframes tgBillUp{from{opacity:0;transform:translateY(16px) scale(.985)}to{opacity:1;transform:none}}
@keyframes tgBillDraw{to{stroke-dashoffset:0}}
@keyframes tgBillFade{to{opacity:1}}
@keyframes tgBillGrow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes tgBillPing{0%{box-shadow:0 0 0 0 var(--tgb-ping)}70%{box-shadow:0 0 0 9px transparent}100%{box-shadow:0 0 0 0 transparent}}
.tgBill-in{animation:tgBillUp .7s cubic-bezier(.16,1,.3,1) both}
.tgBill-in1{animation-delay:.04s}.tgBill-in2{animation-delay:.1s}.tgBill-in3{animation-delay:.18s}.tgBill-in4{animation-delay:.26s}
.tgBill-draw{animation:tgBillDraw 1.15s cubic-bezier(.16,1,.3,1) .15s both}
.tgBill-fade{opacity:0;animation:tgBillFade .7s cubic-bezier(.16,1,.3,1) .8s forwards}
.tgBill-fade2{opacity:0;animation:tgBillFade .45s cubic-bezier(.16,1,.3,1) 1.2s forwards}
.tgBill-grow{transform-origin:left center;animation:tgBillGrow .8s cubic-bezier(.16,1,.3,1) .25s both}
.tgBill-live{animation:tgBillPing 1.8s ease-out infinite}
@keyframes tgBillBar{0%{transform:scaleY(0)}72%{transform:scaleY(1.06)}100%{transform:scaleY(1)}}
.tgBill-bar{transform-origin:center bottom;animation:tgBillBar .75s cubic-bezier(.16,1,.3,1) both}
@keyframes tgBillGlow{0%,100%{filter:drop-shadow(0 0 2px var(--tgb-glow0))}50%{filter:drop-shadow(0 0 10px var(--tgb-glow1))}}
@keyframes tgBillShine{from{transform:translateX(-140%) skewX(-16deg)}to{transform:translateX(380%) skewX(-16deg)}}
/* draw, then breathe — the graphical dashboard's tgw-gact */
.tgBill-gact{animation:tgBillDraw 1.2s cubic-bezier(.16,1,.3,1) both, tgBillGlow 2.8s 1.2s ease-in-out infinite}
.tgBill-shine{position:absolute;top:0;left:0;width:60%;height:100%;pointer-events:none;
  background:linear-gradient(105deg,transparent 30%,rgba(255,255,255,.22) 50%,transparent 68%);
  animation:tgBillShine 1.6s cubic-bezier(.16,1,.3,1) .3s}
.tgBill-row{transition:background 180ms cubic-bezier(.16,1,.3,1),transform 180ms cubic-bezier(.16,1,.3,1),opacity 180ms}
.tgBill-race:hover .tgBill-row{opacity:.5}
.tgBill-race .tgBill-row:hover,.tgBill-race .tgBill-row:focus-within{opacity:1;transform:translateX(6px);background:var(--tgb-rowbg)}
.tgBill-go{opacity:0;transition:opacity 180ms}
.tgBill-row:hover .tgBill-go,.tgBill-row:focus-within .tgBill-go{opacity:1}
@media (prefers-reduced-motion:reduce){
  [class*=tgBill-]{animation:none!important;opacity:1!important;transform:none!important;stroke-dashoffset:0!important}
}`;

/** Drop once inside the billing surface to enable the classes above. */
export function BillingMotion() {
  return <style>{MOTION_CSS}</style>;
}

/* The billing surface borrows the graphical dashboard's paper. Settings runs on
   white cards over a near-white canvas, which is exactly the flatness viz.jsx's
   paper() was written to fix: a 3% lightness step is below the point where an
   edge reads as an edge, so nothing appears to sit on anything. A cooler,
   deeper canvas plus a lighter card gives the page three real levels. */
export function billingPaper(t) {
  if (t.isDark) return { canvas: t.c.bg, card: t.c.surface, well: t.c.surfaceMuted };
  return { canvas: "#D4DBE6", card: "#E8EDF5", well: "#DCE3EE" };
}

export function resolveGateway(billing = {}) {
  const isRazorpay =
    billing.paymentGateway === "razorpay" || Boolean(billing.razorpayCustomerId);
  // Some orgs have neither gateway set (never checked out). Calling those
  // "Stripe" would be a straight-up lie, so the rail hides the row instead.
  const configured = Boolean(
    billing.paymentGateway || billing.razorpayCustomerId || billing.stripeCustomerId
  );
  return {
    configured,
    isRazorpay,
    isStripe: !isRazorpay,
    name: isRazorpay ? "Razorpay" : "Stripe",
    // Only Stripe has a hosted portal to send the admin to.
    hasPortal: !isRazorpay,
    customerId: isRazorpay ? billing.razorpayCustomerId : billing.stripeCustomerId,
    subscriptionId: isRazorpay
      ? billing.razorpaySubscriptionId
      : billing.stripeSubscriptionId,
  };
}

const RZP_METHOD = {
  card: "Card",
  upi: "UPI",
  netbanking: "Net banking",
  wallet: "Wallet",
  emi: "EMI",
  paylater: "Pay later",
};

/** What to show under "Payment method", resolved per gateway. */
export function resolvePaymentMethod(billing = {}, invoices = [], gw) {
  if (billing.paymentBypassEnabled) return { label: "Not required", sub: "billing bypassed" };

  if (gw.isStripe) {
    const pm = billing.defaultPaymentMethod || (billing.paymentMethods || [])[0];
    const card = pm?.card || pm;
    if (card?.last4) {
      const brand = String(card.brand || "Card");
      return {
        label: `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ·· ${card.last4}`,
        sub: card.exp_month
          ? `expires ${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`
          : null,
      };
    }
    return { label: billing.stripeCustomerId ? "On file" : "Not added", sub: null };
  }

  // Razorpay exposes no payment-methods API. Every captured payment carries
  // the instrument it was made with, so the most recent one is the honest
  // answer — labelled as such rather than presented as a saved method.
  const last = (invoices || []).find((i) => i.method);
  if (last) return { label: RZP_METHOD[last.method] || last.method, sub: "last payment" };
  return { label: billing.razorpayCustomerId ? "On file" : "Not added", sub: null };
}

/** Monthly spend from real invoices. Both gateways normalise to the same shape
 *  in SettingsV2's fetchInvoices, so this reads one way for both. */
export function billingSpendSeries(invoices, limit = 8) {
  return (invoices || [])
    .map((i) => ({
      t: new Date(i.date).getTime(),
      v: Number(i.amountPaid) || Number(i.amountDue) || 0,
    }))
    .filter((r) => Number.isFinite(r.t) && r.v > 0)
    .sort((a, b) => a.t - b.t)
    .slice(-limit)
    .map((r) => ({
      // Day + month: two invoices can land in the same month (a mid-cycle seat
      // purchase), and two bars both labelled "Aug" is unreadable.
      label: new Date(r.t).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      value: r.v,
    }));
}

/** Mirrors viz.jsx's smooth() — midpoint cubic through each pair. Duplicated
 *  rather than imported so Settings doesn't pull in the graphical screens. */
function smoothPath(pts, closeY) {
  if (!pts.length) return "";
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  if (closeY != null) d += ` L${pts[pts.length - 1][0]},${closeY} L${pts[0][0]},${closeY} Z`;
  return d;
}

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── Seat gauge ────────────────────────────────────────────────────────────
   The graphical dashboard's Gauge, pointed at seats: an 18px gradient arc with
   the count inside. It gives the rail a focal object instead of another row,
   and it turns gold the moment the last seat goes. */
export function SeatGauge({ t, used, paid, unlimited }) {
  const pct = unlimited ? 4 : paid > 0 ? Math.min(100, Math.round((used / paid) * 100)) : 0;
  const CIRC = 352;
  const filled = Math.round((pct / 100) * 188);
  const full = !unlimited && paid > 0 && used >= paid;
  const gid = full ? "tgBillGaugeWarn" : "tgBillGaugeOk";
  const glow = full ? t.status.warning : t.c.primary;

  return (
    <div style={{ padding: "2px 18px 12px", textAlign: "center" }}>
      {/* viewBox starts at y -12: the 18px stroke overhangs the arc apex by 9px */}
      <svg viewBox="0 -12 268 138" width="100%" height="104" role="img"
        aria-label={unlimited ? "Unlimited seats" : `${used} of ${paid} seats in use`}>
        <defs>
          <linearGradient id="tgBillGaugeOk" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={t.isDark ? "#9BB8EF" : "#789FE8"} />
            <stop offset="100%" stopColor={t.c.primaryHover} />
          </linearGradient>
          <linearGradient id="tgBillGaugeWarn" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#F4D078" />
            <stop offset="100%" stopColor={t.status.warning} />
          </linearGradient>
        </defs>
        <path d="M22 112 A112 112 0 0 1 246 112" fill="none" stroke={t.c.surfaceMuted} strokeWidth="18" strokeLinecap="round" />
        <path
          className="tgBill-gact"
          d="M22 112 A112 112 0 0 1 246 112"
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth="18"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${CIRC}`}
          strokeDashoffset={filled}
          style={{ "--tgb-glow0": `${glow}26`, "--tgb-glow1": `${glow}73` }}
        />
        <text x="134" y="90" textAnchor="middle" fontSize="24" fontWeight="720" fill={t.c.text}>
          {unlimited ? "\u221E" : used}
        </text>
        <text x="134" y="108" textAnchor="middle" fontSize="10" fontWeight="800" letterSpacing="1.2" fill={t.c.textTertiary}>
          {unlimited ? "UNLIMITED SEATS" : `OF ${paid} SEATS`}
        </text>
      </svg>
      <div style={{ fontSize: 12, color: t.c.textTertiary, marginTop: -4 }}>
        {unlimited
          ? "billing bypassed"
          : full
            ? <b style={{ color: t.status.warning }}>No seats left</b>
            : <><b style={{ color: t.c.text }}>{Math.max(0, paid - used)}</b> available</>}
      </div>
    </div>
  );
}

/* ── Spend ────────────────────────────────────────────────────────
   Bars, not a curve. Monthly invoices are discrete events - a line between them
   claims a continuity that isn't there, and one projected charge far larger than
   the history flattens every real bar into the baseline. Bars survive that jump,
   and they match the dashboard's WeekStream. */
export function SpendBars({ t, series, projected, symbol }) {
  const [hover, setHover] = React.useState(null);
  const rows = React.useMemo(() => {
    const out = series.map((r) => ({ ...r, isProj: false }));
    if (projected > 0) out.push({ label: "Next", value: projected, isProj: true });
    return out;
  }, [series, projected]);

  if (!rows.length) {
    return (
      <div style={{ padding: "34px 12px", textAlign: "center", color: t.c.textTertiary, fontSize: 12.5 }}>
        No paid invoices yet - spend appears here after the first payment clears.
      </div>
    );
  }

  const max = Math.max(...rows.map((r) => r.value)) || 1;

  return (
    <div
      style={{
        display: "flex", alignItems: "flex-end", gap: 12,
        height: 194, paddingTop: 24,
        // A baseline so the bars sit on something rather than floating in the card.
        borderBottom: `1px solid ${t.c.border}`,
      }}
    >
      {rows.map((r, i) => {
        const h = Math.max(5, Math.round((r.value / max) * 138));
        const on = hover === i;
        return (
          <div
            key={`${r.label}-${i}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}
          >
            <div
              style={{
                fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                color: on ? t.c.text : t.c.textTertiary,
                opacity: on || rows.length <= 8 ? 1 : 0,
                transition: "color 160ms, opacity 160ms", whiteSpace: "nowrap",
              }}
            >
              {symbol}{Math.round(r.value).toLocaleString("en-US")}
            </div>
            <div
              className="tgBill-bar"
              style={{
                width: "100%", height: h, borderRadius: "7px 7px 3px 3px",
                animationDelay: `${0.06 * i}s`,
                ...(r.isProj
                  ? {
                      // The charge that hasn't happened yet is drawn as an outline,
                      // so it can never be mistaken for money already paid.
                      background: `repeating-linear-gradient(135deg, ${t.c.primary}22 0 5px, transparent 5px 10px)`,
                      border: `1.5px dashed ${t.c.primary}`,
                      boxSizing: "border-box",
                    }
                  : {
                      background: `linear-gradient(180deg, ${t.isDark ? "#9BB8EF" : "#789FE8"}, ${t.c.primaryHover})`,
                      opacity: on ? 1 : 0.9,
                      transition: "opacity 160ms",
                    }),
              }}
            />
            <div
              style={{
                fontSize: 10.5, color: t.c.textTertiary, whiteSpace: "nowrap",
                opacity: r.isProj ? 0.7 : 1, marginBottom: -22, paddingTop: 8,
              }}
            >
              {r.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Attention banner ──────────────────────────────────────────────────────
   Only rendered when something actually needs doing, and it carries the action
   rather than just describing the state. */
export function BillingBanner({ t, billing, gw, statusKey, symbol, onRetry, onResubscribe, onAddMethod, onBuySeats }) {
  const wrap = (tone, body) => (
    <div
      role="status"
      style={{
        // A full soft-tint field turns muddy on a light card - the warning tint
        // in particular reads as olive. Keep the card clean and carry the colour
        // on the left edge and the glyph instead.
        display: "flex", gap: 14, padding: 18,
        borderRadius: t.r.card, marginBottom: 16, fontSize: 12.5, lineHeight: 1.5,
        color: t.c.text, background: billingPaper(t).card,
        border: `1px solid ${t.c.border}`, boxShadow: t.shadow.raised,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 36, height: 36, borderRadius: 11, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: tone.ink, color: "#fff",
          boxShadow: `0 6px 16px -9px ${tone.ink}`,
        }}
      >
        <AlertTriangle size={17} />
      </span>
      <div style={{ flex: 1, minWidth: 0, alignSelf: "center" }}>{body}</div>
    </div>
  );
  const tone = (hex, softKey) => ({ ink: hex, soft: t.status[softKey], line: hex });

  if (billing.paymentBypassEnabled) {
    return wrap(tone(t.c.textTertiary, "neutralSoft"), (
      <>
        <b>Billing is bypassed for this organisation.</b>
        <div style={{ color: t.c.textSecondary, marginTop: 3 }}>Seats are unlimited and no charge is raised.</div>
      </>
    ));
  }

  if (statusKey === "past_due" || statusKey === "unpaid") {
    return wrap(tone(t.status.negative, "negativeSoft"), (
      <>
        <b>Payment failed — every member is locked out.</b>
        <div style={{ color: t.c.textSecondary, marginTop: 3 }}>
          Admins can still sign in, so this usually reaches you as one person&rsquo;s password
          problem. Access returns the moment the charge clears.
        </div>
        {onRetry && (
          <div style={{ marginTop: 9 }}>
            <button type="button" onClick={onRetry} style={pillBtn(t, true)}>
              {gw.hasPortal ? "Update payment method" : "Retry payment"}
            </button>
          </div>
        )}
      </>
    ));
  }

  if (statusKey === "canceled") {
    const ends = billing.currentPeriodEnd
      ? new Date(billing.currentPeriodEnd).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
      : null;
    return wrap(tone(t.c.textTertiary, "neutralSoft"), (
      <>
        <b>Cancelled{ends ? ` — access runs until ${ends}.` : "."}</b>
        <div style={{ color: t.c.textSecondary, marginTop: 3 }}>
          Nothing further is charged. Members lose access the following day.
        </div>
        {onResubscribe && (
          <div style={{ marginTop: 9 }}>
            <button type="button" onClick={onResubscribe} style={pillBtn(t, false)}>Resubscribe</button>
          </div>
        )}
      </>
    ));
  }

  if (statusKey === "trialing") {
    const end = billing.trialEnd || billing.userTrialEndDate;
    const days = end ? Math.ceil((new Date(end) - new Date()) / 86400000) : null;
    return wrap(tone(t.c.primary, "infoSoft"), (
      <>
        <b>
          {days == null ? "Trial in progress." : days < 0 ? "Trial has ended." : days === 0 ? "Trial ends today." : `${days} ${days === 1 ? "day" : "days"} left in the trial.`}
        </b>
        <div style={{ color: t.c.textSecondary, marginTop: 3 }}>
          Add a payment method to keep your team&rsquo;s access uninterrupted.
        </div>
        {onAddMethod && (
          <div style={{ marginTop: 9 }}>
            <button type="button" onClick={onAddMethod} style={pillBtn(t, true)}>Add payment method</button>
          </div>
        )}
      </>
    ));
  }

  if ((billing.remainingSlots || 0) <= 0 && billing.orgPlanSelection && !billing.paymentBypassEnabled) {
    const paid = billing.paidMemberSlots || 1;
    const per = Number(billing.orgPlanAmount) || 0;
    const ppr = billingPaper(t);
    /* Same well | card split the rail and HeroBand use: the figure sits in the
       inset panel, the message and the action in the lighter one. A flat strip
       with a coloured edge kept reading as a toast; this reads as part of the
       page. */
    return (
      <div
        role="status"
        style={{
          display: "grid", gridTemplateColumns: "minmax(150px, 190px) 1fr",
          borderRadius: t.r.card, marginBottom: 16, overflow: "hidden",
          background: ppr.card, border: `1px solid ${t.c.border}`,
          boxShadow: t.shadow.raised,
        }}
      >
        <div
          style={{
            padding: "18px 18px 16px", background: ppr.well,
            borderRight: `1px solid ${t.c.border}`,
          }}
        >
          <div style={{ ...t.font.statLabel, color: t.status.warning }}>Seats</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 2, marginTop: 9 }}>
            <span style={{ fontSize: 28, fontWeight: 720, letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {paid}
            </span>
            <span style={{ fontSize: 15, fontWeight: 600, color: t.c.textTertiary }}>/ {paid}</span>
          </div>
          <div
            style={{
              display: "flex", height: 8, marginTop: 12,
              borderRadius: 4, overflow: "hidden", background: t.c.surfaceMuted,
            }}
          >
            <i
              className="tgBill-grow"
              style={{
                display: "block", flex: 1, height: "100%",
                background: `linear-gradient(90deg, #F4D078, ${t.status.warning})`,
              }}
            />
          </div>
          <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 8 }}>
            0 available
          </div>
        </div>

        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                aria-hidden
                style={{
                  width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: t.accentPairs.yellow.bg, color: t.accentPairs.yellow.fg,
                }}
              >
                <AlertTriangle size={14} />
              </span>
              <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Every seat is taken
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: t.c.textSecondary, marginTop: 6, lineHeight: 1.55, maxWidth: 460 }}>
              New invitations will be blocked until you add seats. Everyone already on
              the team keeps working as normal.
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={onBuySeats}
              style={{ ...pillBtn(t, true), height: 34, padding: "0 16px", fontSize: 13, borderRadius: 9 }}
            >
              <ShoppingCart size={14} />
              Buy seats
            </button>
            {per > 0 && (
              <span style={{ fontSize: 12.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                <b style={{ color: t.c.text, fontWeight: 600 }}>{symbol}{per.toLocaleString("en-US")}</b>
                {" "}per extra seat, per month
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function pillBtn(t, primary) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6, height: 27, padding: "0 11px",
    borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500,
    fontFamily: "inherit",
    border: `1px solid ${primary ? t.c.primary : t.c.border}`,
    background: primary ? t.c.primary : t.c.surface,
    color: primary ? t.c.textInverse : t.c.text,
  };
}

/* ── Sticky summary rail ───────────────────────────────────────────────────
   Money, status and the two actions stay on screen while the admin scrolls
   through invoices — the thing they came to check is never scrolled away. */
export function BillingRail({
  t, billing, gw, symbol, statusKey, statusLabel, monthlyTotal, payMethod,
  onManage, onBuySeats,
}) {
  const used = billing.usedMemberSlots || 0;
  const paid = billing.paidMemberSlots || 0;
  const bypass = Boolean(billing.paymentBypassEnabled);

  const tone =
    statusKey === "active" ? t.status.positive
      : statusKey === "trialing" ? t.status.info
        : statusKey === "past_due" || statusKey === "unpaid" ? t.status.negative
          : t.status.neutral;

  const row = (k, v, sub) => (
    <div key={k} style={{ display: "flex", alignItems: "center", gap: 9, padding: "12px 18px", borderBottom: `1px solid ${t.c.border}` }}>
      <span style={{ fontSize: 12, color: t.c.textTertiary, flex: 1 }}>{k}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: "right", color: t.c.text }}>
        {v}
        {sub && <span style={{ display: "block", fontSize: 11, fontWeight: 400, color: t.c.textTertiary }}>{sub}</span>}
      </span>
    </div>
  );

  return (
    <aside
      className="tgBill-in tgBill-in2"
      style={{
        position: "sticky", top: 16, alignSelf: "start",
        background: billingPaper(t).card, border: `1px solid ${t.c.border}`,
        borderRadius: t.r.card, boxShadow: t.shadow.raised, overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", overflow: "hidden", padding: "18px 18px 16px", background: billingPaper(t).well, borderBottom: `1px solid ${t.c.border}` }}>
        {/* Ambient wash in the status colour — the rail reads as an object
            rather than a panel, and it re-tints when the status changes. */}
        <span
          aria-hidden
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: `radial-gradient(340px 150px at 12% -35%, ${tone}33, transparent 70%)`,
            transition: "background 500ms cubic-bezier(.16,1,.3,1)",
          }}
        />
        <span aria-hidden className="tgBill-shine" />
        <div style={{ position: "relative", ...t.font.statLabel, color: t.c.primary }}>Monthly cost</div>
        <div style={{ position: "relative", display: "flex", alignItems: "baseline", gap: 3, marginTop: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: t.c.textTertiary }}>{symbol}</span>
          <span style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.035em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {money(monthlyTotal)}
          </span>
          <span style={{ fontSize: 12, color: t.c.textTertiary, marginLeft: 5 }}>/ mo</span>
        </div>
        {bypass && <div style={{ position: "relative", fontSize: 11.5, color: t.c.textTertiary, marginTop: 8 }}>No charge raised</div>}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "12px 18px", borderBottom: `1px solid ${t.c.border}` }}>
        <span
          className={statusKey === "past_due" || statusKey === "trialing" ? "tgBill-live" : undefined}
          style={{ width: 8, height: 8, borderRadius: 999, background: tone, flex: "none", "--tgb-ping": `${tone}66` }}
        />
        <span style={{ fontSize: 12, color: t.c.textTertiary, flex: 1 }}>Status</span>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{statusLabel}</span>
      </div>

      {billing.currentPeriodEnd && row(
        statusKey === "canceled" ? "Ends" : "Renews",
        new Date(billing.currentPeriodEnd).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
      )}
      {row("Payment method", payMethod.label, payMethod.sub)}
      {gw.configured && row("Gateway", gw.name)}
      {!bypass && row("Per seat", `${symbol}${billing.orgPlanAmount || 0} / mo`)}

      <SeatGauge
        t={t}
        used={used}
        paid={paid}
        unlimited={bypass}
      />

      <div style={{ padding: "14px 18px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {onManage && (
          <button type="button" onClick={onManage} style={{ ...railBtn(t, true), width: "100%" }}>
            <CreditCard size={13} />
            {gw.hasPortal ? (statusKey === "canceled" ? "Resubscribe" : "Manage subscription") : (statusKey === "canceled" ? "Resume subscription" : "Manage subscription")}
          </button>
        )}
        <button type="button" onClick={onBuySeats} style={{ ...railBtn(t, false), width: "100%" }}>
          <ShoppingCart size={13} />
          Buy seats
        </button>
        {gw.configured && !gw.hasPortal && (
          <div style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 11, color: t.c.textTertiary, marginTop: 2 }}>
            <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
            Razorpay has no hosted portal — changes are made here.
          </div>
        )}
      </div>
    </aside>
  );
}

function railBtn(t, primary) {
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    height: 32, padding: "0 13px", borderRadius: 9, cursor: "pointer",
    fontSize: 12.5, fontWeight: 500, fontFamily: "inherit",
    border: `1px solid ${primary ? t.c.primary : t.c.border}`,
    background: primary ? t.c.primary : t.c.surface,
    color: primary ? t.c.textInverse : t.c.text,
    transition: "background 160ms, border-color 160ms",
  };
}

/* ── Next invoice ─────────────────────────────────────────────────────────── */
export function NextInvoiceLines({ t, billing, symbol, monthlyTotal }) {
  const bypass = Boolean(billing.paymentBypassEnabled);
  const seats = bypass ? 1 : (billing.paidMemberSlots || 1);
  const plan = billing.orgPlanSelection
    ? billing.orgPlanSelection.charAt(0).toUpperCase() + billing.orgPlanSelection.slice(1)
    : "Plan";
  const li = (label, value, strong) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: strong ? "14px 0 0" : "11px 0", fontSize: 13.5, borderTop: `1px solid ${strong ? t.c.borderStrong : t.c.border}`, fontWeight: strong ? 700 : 400 }}>
      <span style={{ color: strong ? t.c.text : t.c.textSecondary }}>{label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "0 0 11px", fontSize: 13.5 }}>
        <span style={{ color: t.c.textSecondary }}>{plan} × {seats} {seats === 1 ? "seat" : "seats"}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{symbol}{money(bypass ? 0 : monthlyTotal)}</span>
      </div>
      {li("Total due", `${symbol}${money(bypass ? 0 : monthlyTotal)}`, true)}
    </div>
  );
}
